import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { canonicalMoney, sumMoney, subtractMoney } from '../lib/money';

function formatDate(d: Date): string { return d.toISOString().split('T')[0]; }

function getPeriodRange(period: string): { start: string; end: string } {
  const now = new Date();
  const end = formatDate(now);
  let start: Date;

  if (period === 'month') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (period === 'quarter') {
    const qMonth = Math.floor(now.getMonth() / 3) * 3;
    start = new Date(now.getFullYear(), qMonth, 1);
  } else if (period === 'ytd') {
    start = new Date(now.getFullYear(), 0, 1);
  } else {
    // custom fallback to ytd
    start = new Date(now.getFullYear(), 0, 1);
  }
  return { start: formatDate(start), end };
}

interface MonthlyRow { month: string; revenue: number; expenses: number }
interface CategoryRow { category: string; amount: number }

interface UseRevenueResult {
  revenue: number;
  expenses: number;
  netProfit: number;
  hstCollected: number;
  hstOnExpenses: number;
  hstOwing: number;
  revenueByMonth: MonthlyRow[];
  expensesByCategory: CategoryRow[];
  period: string;
  setPeriod: (p: string) => void;
  loading: boolean;
}

export function useRevenue(businessId: string | undefined): UseRevenueResult {
  const [period, setPeriod] = useState('ytd');
  const [ledgerIn, setLedgerIn] = useState<{ amount: number; date: string }[]>([]);
  const [expenseRows, setExpenseRows] = useState<{ amount: number; tax_amount: number; date: string; category: string }[]>([]);
  const [paidInvoiceTax, setPaidInvoiceTax] = useState(0);
  const [loading, setLoading] = useState(true);

  const { start, end } = useMemo(() => getPeriodRange(period), [period]);

  const fetchData = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [ledgerRes, expenseRes, invoiceTaxRes] = await Promise.all([
        supabase
          .from('payment_ledger')
          .select('amount, date')
          .eq('business_id', businessId)
          .eq('direction', 'in')
          .gte('date', start)
          .lte('date', end),
        supabase
          .from('expenses')
          .select('amount, tax_amount, date, category')
          .eq('business_id', businessId)
          .eq('archived', false)
          .gte('date', start)
          .lte('date', end),
        supabase
          .from('invoices')
          .select('tax_amount')
          .eq('business_id', businessId)
          .eq('status', 'paid')
          .gte('paid_at', start + 'T00:00:00')
          .lte('paid_at', end + 'T23:59:59'),
      ]);

      setLedgerIn((ledgerRes.data ?? []) as { amount: number; date: string }[]);
      setExpenseRows((expenseRes.data ?? []) as { amount: number; tax_amount: number; date: string; category: string }[]);
      // Rule #34: canonical money sum; Number() at consumer boundary.
      setPaidInvoiceTax(Number(sumMoney((invoiceTaxRes.data ?? []).map((i: { tax_amount: number | null }) => canonicalMoney(i.tax_amount ?? 0)))));
    } catch {
      // keep empty state
    } finally { setLoading(false); }
  }, [businessId, start, end]);

  useEffect(() => { void fetchData(); }, [fetchData]);

  // Rule #34: canonical money aggregation through the wrapper; Number() at the
  // consumer boundary since downstream consumers still expect numbers.
  const revenue = useMemo(() => Number(sumMoney(ledgerIn.map(r => canonicalMoney(r.amount)))), [ledgerIn]);
  const expenses = useMemo(() => Number(sumMoney(expenseRows.map(e => canonicalMoney(e.amount)))), [expenseRows]);
  const netProfit = Number(subtractMoney(canonicalMoney(revenue), canonicalMoney(expenses)));
  const hstCollected = paidInvoiceTax;
  const hstOnExpenses = useMemo(() => Number(sumMoney(expenseRows.map(e => canonicalMoney(e.tax_amount ?? 0)))), [expenseRows]);
  const hstOwing = Number(subtractMoney(canonicalMoney(hstCollected), canonicalMoney(hstOnExpenses)));

  const revenueByMonth = useMemo<MonthlyRow[]>(() => {
    const map = new Map<string, { revenue: number; expenses: number }>();

    for (const r of ledgerIn) {
      const m = r.date.slice(0, 7); // YYYY-MM
      const entry = map.get(m) ?? { revenue: 0, expenses: 0 };
      entry.revenue += r.amount;
      map.set(m, entry);
    }
    for (const e of expenseRows) {
      const m = e.date.slice(0, 7);
      const entry = map.get(m) ?? { revenue: 0, expenses: 0 };
      entry.expenses += e.amount;
      map.set(m, entry);
    }

    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, data]) => ({ month, ...data }));
  }, [ledgerIn, expenseRows]);

  const expensesByCategory = useMemo<CategoryRow[]>(() => {
    const map = new Map<string, number>();
    for (const e of expenseRows) {
      map.set(e.category, (map.get(e.category) ?? 0) + e.amount);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([category, amount]) => ({ category, amount }));
  }, [expenseRows]);

  return { revenue, expenses, netProfit, hstCollected, hstOnExpenses, hstOwing, revenueByMonth, expensesByCategory, period, setPeriod, loading };
}
